import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DeliveryNoteForm = ({ orders, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    orderId: '',
    customerName: '',
    customerContact: '',
    deliveryDate: new Date().toISOString().split('T')[0],
    products: [],
    deliveryAddress: {
      street: '',
      city: '',
      postalCode: '',
      country: 'España'
    },
    instructions: '',
    driverName: '',
    vehiclePlate: ''
  });

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  const handleOrderSelect = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      setFormData(prev => ({
        ...prev,
        orderId: order.id,
        customerName: order.customerName,
        products: order.products.map(p => ({
          ...p,
          verified: false,
          scannedQuantity: 0
        })),
        deliveryAddress: order.deliveryAddress
      }));
    }
  };

  const handleProductVerification = (productIndex, verified) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map((product, index) =>
        index === productIndex ? { ...product, verified } : product
      )
    }));
  };

  const handleQuantityUpdate = (productIndex, quantity) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map((product, index) =>
        index === productIndex ? { ...product, scannedQuantity: quantity } : product
      )
    }));
  };

  const handleBarcodeScanning = (productIndex) => {
    setIsScanning(true);
    // Simulate barcode scanning
    setTimeout(() => {
      handleProductVerification(productIndex, true);
      setIsScanning(false);
    }, 2000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.orderId || !formData.driverName || !formData.vehiclePlate) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }

    const allProductsVerified = formData.products.every(p => p.verified);
    if (!allProductsVerified) {
      alert('Por favor verifica todos los productos antes de crear el albarán');
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="bg-surface rounded-lg border border-border">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading-semibold text-text-primary">
            Crear Nuevo Albarán
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onCancel}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-6">
        {/* Order Selection */}
        <div>
          <label className="block text-sm font-body-medium text-text-primary mb-2">
            Seleccionar Pedido *
          </label>
          <select
            value={formData.orderId}
            onChange={(e) => handleOrderSelect(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="">Selecciona un pedido...</option>
            {orders.map(order => (
              <option key={order.id} value={order.id}>
                {order.id} - {order.customerName}
              </option>
            ))}
          </select>
        </div>

        {selectedOrder && (
          <>
            {/* Customer Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Cliente
                </label>
                <Input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                  readOnly
                  className="bg-secondary-50"
                />
              </div>
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Contacto
                </label>
                <Input
                  type="email"
                  value={formData.customerContact}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerContact: e.target.value }))}
                  placeholder="email@cliente.com"
                />
              </div>
            </div>

            {/* Delivery Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Fecha de Entrega *
                </label>
                <Input
                  type="date"
                  value={formData.deliveryDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, deliveryDate: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Conductor *
                </label>
                <Input
                  type="text"
                  value={formData.driverName}
                  onChange={(e) => setFormData(prev => ({ ...prev, driverName: e.target.value }))}
                  placeholder="Nombre del conductor"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Matrícula del Vehículo *
                </label>
                <Input
                  type="text"
                  value={formData.vehiclePlate}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehiclePlate: e.target.value }))}
                  placeholder="1234-ABC"
                  required
                />
              </div>
            </div>

            {/* Products Verification */}
            <div>
              <h3 className="text-sm font-body-medium text-text-primary mb-3">
                Verificación de Productos
              </h3>
              <div className="space-y-3">
                {formData.products.map((product, index) => (
                  <div key={index} className="p-4 border border-border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="text-sm font-body-medium text-text-primary">
                          {product.name}
                        </div>
                        <div className="text-xs text-text-muted">
                          SKU: {product.sku} | Cantidad: {product.quantity}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {product.verified ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-body-medium bg-success-100 text-success">
                            <Icon name="CheckCircle" size={12} className="mr-1" />
                            Verificado
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-body-medium bg-warning-100 text-warning">
                            <Icon name="AlertCircle" size={12} className="mr-1" />
                            Pendiente
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="flex-1">
                        <Input
                          type="number"
                          value={product.scannedQuantity}
                          onChange={(e) => handleQuantityUpdate(index, parseInt(e.target.value) || 0)}
                          placeholder="Cantidad verificada"
                          min="0"
                          max={product.quantity}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleBarcodeScanning(index)}
                        disabled={isScanning}
                        iconName={isScanning ? "Loader2" : "Scan"}
                        className={isScanning ? "animate-spin" : ""}
                      >
                        Escanear
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          handleQuantityUpdate(index, product.quantity);
                          handleProductVerification(index, true);
                        }}
                        iconName="CheckCircle"
                      >
                        Verificar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Address */}
            <div>
              <h3 className="text-sm font-body-medium text-text-primary mb-3">
                Dirección de Entrega
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input
                    type="text"
                    value={formData.deliveryAddress.street}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deliveryAddress: { ...prev.deliveryAddress, street: e.target.value }
                    }))}
                    placeholder="Calle y número"
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    value={formData.deliveryAddress.city}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deliveryAddress: { ...prev.deliveryAddress, city: e.target.value }
                    }))}
                    placeholder="Ciudad"
                  />
                </div>
                <div>
                  <Input
                    type="text"
                    value={formData.deliveryAddress.postalCode}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deliveryAddress: { ...prev.deliveryAddress, postalCode: e.target.value }
                    }))}
                    placeholder="Código postal"
                  />
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-body-medium text-text-primary mb-2">
                Instrucciones de Entrega
              </label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                placeholder="Instrucciones especiales para la entrega..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!formData.products.every(p => p.verified)}
              >
                Crear Albarán
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default DeliveryNoteForm;